/*
 Il nome con cui salvare un file scaricato, letto dall'intestazione Content-Disposition.

 Il backend la compone come vuole RFC 6266 (`DownloadFileName` nel repo backend):

   attachment; filename="perche.pdf"; filename*=UTF-8''perch%C3%A9.pdf

 Il nome esatto sta solo in `filename*`, codificato per cento; `filename` ne è la copia in
 solo ASCII per chi `filename*` non lo legge, con gli accenti tolti e le emoji ridotte a `_`.
 Il portale non lascia fare al browser - il file arriva come blob da una chiamata XHR e lo si
 salva con un'ancora finta - quindi l'intestazione la legge lui, e leggendo solo `filename`
 salvava `perche.pdf`. Per questo l'ordine è: `filename*`, poi `filename`, poi il nome di
 ripiego di chi chiama.

 Il ripiego non è un caso raro. Una risposta cross-origin consegna alla pagina solo le
 intestazioni che il backend espone con `Access-Control-Expose-Headers`, e oggi Content-
 Disposition è esposta solo da `GET /company/download`: per tutti gli altri download
 l'intestazione qui arriva `undefined`, e il nome lo decide chi chiama.

 Il nome viene da dati caricati da qualcuno (il DVR porta il nome scelto da chi l'ha caricato),
 quindi non è fidato anche se il backend lo ripulisce già: via caratteri di controllo e
 caratteri invisibili di direzione, e i separatori di percorso diventano `_`, perché il nome di
 un file non è un percorso.
*/

// Controllo C0 e C1, e i caratteri di formato che ingannano chi legge: l'inversione di
// direzione (U+202E) fa apparire `fattura<U+202E>fdp.exe` come `fatturaexe.pdf`. Lo zero-width
// joiner (U+200D) resta, come nel backend: tiene insieme le emoji composte.
const CARATTERI_DA_TOGLIERE =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001f\u007f-\u009f\u200b\u200c\u200e\u200f\u2028-\u202e\u2060-\u2069\ufeff]/g;

const SEPARATORI_DI_PERCORSO = /[\\/]/g;

// Punti e spazi agli estremi: un nome che comincia col punto è nascosto (e `..` è la cartella
// sopra), e Windows toglie da sé quelli in fondo, lasciando un file senza estensione.
const ESTREMI = /^[\s.]+|[\s.]+$/g;

export function nomeFileScaricato(
  contentDisposition: unknown,
  ripiego: string
): string {
  const parametri =
    typeof contentDisposition === "string"
      ? parametriDi(contentDisposition)
      : new Map<string, string>();

  const esteso = parametri.get("filename*");
  const candidati = [
    esteso !== undefined ? decodificaRfc5987(esteso) : undefined,
    parametri.get("filename"),
  ];
  for (const candidato of candidati) {
    const pulito = pulisci(candidato);
    if (pulito) return pulito;
  }
  return pulisci(ripiego) || ripiego;
}

/*
 I parametri dell'intestazione, per nome in minuscolo. Si scorre a mano invece di usare
 un'espressione regolare perché un nome fra virgolette può contenere `;`, `=` e virgolette
 protette da `\`, e una regex che spezza sul primo `;` taglia il nome a metà.
*/
function parametriDi(intestazione: string): Map<string, string> {
  const parametri = new Map<string, string>();
  // Il primo pezzo è il tipo (`attachment`), salvo un'intestazione che parte già dai parametri.
  const primoPunto = intestazione.indexOf(";");
  const primoUguale = intestazione.indexOf("=");
  let i =
    primoUguale !== -1 && (primoPunto === -1 || primoUguale < primoPunto)
      ? 0
      : primoPunto === -1
        ? -1
        : primoPunto + 1;
  while (i !== -1 && i < intestazione.length) {
    const uguale = intestazione.indexOf("=", i);
    if (uguale === -1) break;
    const pezzo = intestazione.slice(i, uguale);
    // Un parametro senza valore (`attachment; x; filename=...`): si salta al successivo.
    if (pezzo.includes(";")) {
      i = i + pezzo.lastIndexOf(";") + 1;
      continue;
    }
    const nome = pezzo.trim().toLowerCase();
    i = uguale + 1;
    while (intestazione[i] === " " || intestazione[i] === "\t") i++;

    let valore = "";
    if (intestazione[i] === '"') {
      i++;
      while (i < intestazione.length && intestazione[i] !== '"') {
        if (intestazione[i] === "\\" && i + 1 < intestazione.length) i++;
        valore += intestazione[i];
        i++;
      }
      i = intestazione.indexOf(";", i);
    } else {
      const fine = intestazione.indexOf(";", i);
      valore = intestazione.slice(i, fine === -1 ? undefined : fine).trim();
      i = fine;
    }
    // Il primo vince: un parametro ripetuto più avanti non scavalca quello già letto.
    if (nome && !parametri.has(nome)) parametri.set(nome, valore);
    if (i !== -1) i++;
  }
  return parametri;
}

/*
 `filename*` in forma RFC 5987: `charset'lingua'valore-codificato-per-cento`. Il backend manda
 sempre UTF-8; ISO-8859-1 è l'altro charset che la norma ammette. Qualunque altra forma, o una
 codifica per cento rotta (`%E9` da solo non è UTF-8 valido e `decodeURIComponent` lancia), vale
 come assente: si ricade su `filename`.
*/
function decodificaRfc5987(valore: string): string | undefined {
  const parti = valore.match(/^([^']*)'[^']*'(.*)$/);
  if (!parti) return undefined;
  const charset = parti[1].toLowerCase();
  const codificato = parti[2];
  try {
    if (charset === "utf-8") return decodeURIComponent(codificato);
    if (charset === "iso-8859-1") {
      return codificato.replace(/%([0-9a-f]{2})/gi, (_, esadecimale: string) =>
        String.fromCharCode(parseInt(esadecimale, 16))
      );
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function pulisci(nome: string | undefined): string | undefined {
  if (!nome) return undefined;
  const pulito = nome
    .replace(CARATTERI_DA_TOGLIERE, "")
    .replace(SEPARATORI_DI_PERCORSO, "_")
    .replace(ESTREMI, "");
  return pulito || undefined;
}
